import React, { useState } from 'react';
import { Box, TextField, Button, Stack } from '@mui/material';
import { DataGrid, GridActionsCellItem } from '@mui/x-data-grid';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import { ModalView } from '../../../providers/modal.provider';
import PropTypes from 'prop-types';
import { TranslationDeleteTranslation, TranslationUpdateTranslation } from './services';

const TranslationPluginList = ({ translations }) => {
  const [loading, setLoading] = useState(false);
  const [rows, setRows] = useState(translations || []);

  const [openDialog, setOpenDialog] = useState(false);
  const [editingRow, setEditingRow] = useState(null);
  const [formData, setFormData] = useState({ transKey: '', transValue: '' });

  const handleEdit = (id) => {
    const row = rows.find((r) => r.id === id);
    if (row) {
      setEditingRow(row);
      setFormData({ transKey: row.transKey, transValue: row.transValue });
      setOpenDialog(true);
    }
  };

  const handleDelete = async (id) => {
    try {
      if (confirm('Are you sure you want to delete this translation?')) {
        setLoading(true);
        const r = await TranslationDeleteTranslation(id);
        setRows(rows.filter((r) => r.id !== id));
      }
    } catch (error) {
      console.error('Error deleting translation:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    if (editingRow) {
      try {
        const r = await TranslationUpdateTranslation({
          ...editingRow,
          transKey: formData.transKey,
          transValue: formData.transValue,
        });

        if (r?.data?.success) {
          setRows(
            rows.map((r) =>
              r.id === editingRow.id
                ? { ...r, transKey: formData.transKey, transValue: formData.transValue }
                : r
            )
          );
          setOpenDialog(false);
          setEditingRow(null);
        }
      } catch (error) {
        console.error('Error updating translation:', error);
      }
    }
  };

  const columns = [
    { field: 'id', headerName: 'ID', minWidth: 70 },
    { field: 'transKey', headerName: 'Trans Key', minWidth: 150, editable: false },
    {
      field: 'transValue',
      headerName: 'Trans Value',
      minWidth: 200,
      flex: 1,
      editable: false,
    },
    {
      field: 'actions',
      type: 'actions',
      headerName: 'Actions',
      width: 100,
      renderCell: (params) => (
        <>
          <GridActionsCellItem
            icon={<EditIcon />}
            label='Edit'
            onClick={() => handleEdit(params.id)}
          />
          <GridActionsCellItem
            icon={<DeleteIcon />}
            label='Delete'
            onClick={() => handleDelete(params.id)}
          />
        </>
      ),
    },
  ];

  return (
    <Box sx={{ minHeight: 400, width: '100%', mt: 4 }}>
      <DataGrid
        rows={rows}
        columns={columns}
        showToolbar
        pageSizeOptions={[5, 10, 25, 50, 100]}
      />

      <ModalView
        fader={openDialog === true ? 'show' : 'hide'}
        onCloseEvent={() => setOpenDialog(false)}
      >
        <Box sx={{ mt: 4, p: 3, minWidth: 300 }}>
          <TextField
            label='Trans Key'
            fullWidth
            value={formData.transKey}
            onChange={(e) => setFormData({ ...formData, transKey: e.target.value })}
            margin='normal'
          />
          <TextField
            label='Trans Value'
            fullWidth
            multiline
            value={formData.transValue}
            onChange={(e) => setFormData({ ...formData, transValue: e.target.value })}
            margin='normal'
          />
          <Stack direction='row' gap={1} sx={{ mt: 2 }}>
            <Button variant='contained' onClick={handleSave}>
              Save
            </Button>
            <Button variant='outlined' onClick={() => setOpenDialog(false)}>
              Cancel
            </Button>
          </Stack>
        </Box>
      </ModalView>
    </Box>
  );
};

TranslationPluginList.propTypes = {
  translations: PropTypes.array.isRequired,
};

export default TranslationPluginList;
